import { Link } from 'react-router-dom'
import { Eye } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { PayNowFlow } from '@/components/member/pay-now-flow'
import { useMemberInvoices } from '@/hooks/use-billing'
import { useInitiateStkPush, usePayNowInfo } from '@/hooks/use-payments'
import { formatKES } from '@/lib/utils'
import type { Invoice, InvoiceStatus } from '@riverpark/types'

function StatusBadge({ status }: { status: InvoiceStatus }) {
  switch (status) {
    case 'paid':
      return <Badge variant="paid">Paid</Badge>
    case 'overdue':
      return <Badge variant="overdue">Overdue</Badge>
    case 'sent':
      return <Badge variant="pending">Sent</Badge>
    case 'voided':
      return <Badge variant="default">Voided</Badge>
    default:
      return <Badge variant="draft">Draft</Badge>
  }
}

function formatDate(unixSeconds?: number) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function AmountCell({ invoice }: { invoice: Invoice }) {
  const isPartial =
    invoice.amountPaid > 0 && invoice.status !== 'paid' && invoice.status !== 'voided'
  if (!isPartial) return <>{formatKES(invoice.amount)}</>
  return (
    <span>
      {formatKES(invoice.balance)} due{' '}
      <span className="text-xs text-muted-foreground">of {formatKES(invoice.amount)}</span>
    </span>
  )
}

interface UtilityGroup {
  utilityTypeId: string | null
  utilityTypeName: string
  invoices: Invoice[]
  balance: number
}

/** Groups in the order their utility type first appears — invoices already arrive sorted
 * newest-first, so this naturally surfaces the most recently active utility type first. */
function groupByUtilityType(invoices: Invoice[]): UtilityGroup[] {
  const groups: UtilityGroup[] = []
  const byKey = new Map<string, UtilityGroup>()
  for (const inv of invoices) {
    const key = inv.utilityTypeId ?? 'none'
    let group = byKey.get(key)
    if (!group) {
      group = {
        utilityTypeId: inv.utilityTypeId,
        utilityTypeName: inv.utilityTypeName ?? 'Other',
        invoices: [],
        balance: 0,
      }
      byKey.set(key, group)
      groups.push(group)
    }
    group.invoices.push(inv)
    if (inv.status !== 'paid' && inv.status !== 'voided') group.balance += inv.balance
  }
  return groups
}

function GroupPayButton({ group }: { group: UtilityGroup }) {
  // Scoped pay-info computes the `{plot}-{invoicePrefix}` reference server-side — members can't
  // call the staff-only /api/utilities/types endpoint themselves to build this client-side.
  const { data: payInfoData } = usePayNowInfo(group.utilityTypeId ?? undefined)
  const payInfo = payInfoData?.data
  const initiate = useInitiateStkPush()

  if (group.balance <= 0 || !group.utilityTypeId || !payInfo) return null

  return (
    <PayNowFlow
      amount={group.balance}
      defaultPhone={payInfo.phone}
      paybill={payInfo.paybill}
      accountReference={payInfo.accountReference}
      isPaying={initiate.isPending}
      onPay={async (phone) => {
        const { data: result } = await initiate.mutateAsync({
          phone,
          target: { utilityTypeId: group.utilityTypeId! },
        })
        return result
      }}
    />
  )
}

export function MemberInvoicesPanel() {
  const { data, isLoading } = useMemberInvoices({ excludeInvoiceType: 'credit_note' })
  const invoices = data?.data ?? []

  if (isLoading) {
    return <p className="font-sans text-sm text-muted-foreground">Loading invoices…</p>
  }
  if (invoices.length === 0) {
    return (
      <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
        No invoices yet.
      </p>
    )
  }

  const groups = groupByUtilityType(invoices)

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.utilityTypeId ?? 'none'}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="font-sans text-sm font-medium text-ink">
              {group.utilityTypeName}
              <span className="ml-2 font-normal text-muted-foreground">
                {group.balance > 0 ? `${formatKES(group.balance)} due` : 'Cleared'}
              </span>
            </p>
            <GroupPayButton group={group} />
          </div>

          {/* Mobile: cards */}
          <div className="space-y-2 sm:hidden">
            {group.invoices.map((inv) => (
              <Link
                key={inv.id}
                to={`/bills/${inv.id}`}
                className="block rounded border border-border-2 bg-card p-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="font-sans text-sm font-medium text-ink">
                      {inv.invoiceNumber}
                    </span>
                    <StatusBadge status={inv.status} />
                  </div>
                  <Eye size={14} className="text-muted-foreground" />
                </div>
                <div className="mt-2 font-sans text-xs text-muted-foreground">
                  <p>
                    <AmountCell invoice={inv} /> · Due {formatDate(inv.dueDate)}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {/* Desktop / tablet */}
          <div className="hidden overflow-x-auto rounded border border-border-2 sm:block">
            <table className="w-full font-sans text-sm">
              <thead>
                <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                  <th className="px-4 py-3">Invoice #</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {group.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-border-2 last:border-0">
                    <td className="px-4 py-3 font-medium text-ink">{inv.invoiceNumber}</td>
                    <td className="px-4 py-3 text-ink">
                      <AmountCell invoice={inv} />
                    </td>
                    <td className="px-4 py-3 text-ink">{formatDate(inv.dueDate)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/bills/${inv.id}`}
                        className="inline-flex h-8 items-center justify-center gap-1.5 rounded border border-border bg-transparent px-3.5 font-sans text-xs font-medium tracking-wide text-ink transition-colors hover:border-ink"
                      >
                        <Eye size={14} />
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}
